import React, { useMemo } from 'react';
import { ComponentRenderer } from '../ComponentRenderer';
import { InsertComponentLine } from '../InsertComponentLine';
import { INVISIBLE_COMPONENT_TYPES } from '../InvisibleComponentsBar';
import type { Component } from '../../schemas/validation';

interface ComponentsListProps {
  /** All components including invisible ones (head-content, body-end) */
  components: Component[];
  onAddComponentAt?: (position: number) => void;
  onEditComponent: (component: Component) => void;
  onDeleteComponent: (componentId: string) => void;
  onMoveComponent: (componentId: string, direction: 'up' | 'down') => void;
  onSaveToLibrary: (component: Component) => void;
  onDuplicateComponent: (component: Component) => void;
}

export const ComponentsList: React.FC<ComponentsListProps> = ({
  components,
  onAddComponentAt,
  onEditComponent,
  onDeleteComponent,
  onMoveComponent,
  onSaveToLibrary,
  onDuplicateComponent,
}) => {
  // Filter out invisible components for rendering
  // But use the full array for calculating insert positions
  const visibleComponents = useMemo(
    () => components.filter(c => !INVISIBLE_COMPONENT_TYPES.includes(c.type)),
    [components]
  );

  /**
   * Get the actual position in the full components array for insertion.
   * This ensures new components are inserted at the correct position
   * even when invisible components exist.
   */
  const getActualInsertPosition = (visibleIndex: number): number => {
    if (visibleIndex === 0) {
      // Insert at the beginning - find the position of the first visible component
      // or insert at 0 if no visible components
      if (visibleComponents.length === 0) {
        return components.length;
      }
      const firstVisible = visibleComponents[0];
      return components.findIndex(c => c.id === firstVisible.id);
    }

    // Insert after the visible component at visibleIndex - 1
    const precedingVisibleComponent = visibleComponents[visibleIndex - 1];
    const actualIndex = components.findIndex(c => c.id === precedingVisibleComponent.id);
    return actualIndex + 1;
  };

  return (
    <>
      {onAddComponentAt && (
        <InsertComponentLine position={getActualInsertPosition(0)} onInsert={onAddComponentAt} />
      )}
      {visibleComponents.map((component, visibleIndex) => (
        <React.Fragment key={component.id}>
          <div className="relative">
            <ComponentRenderer
              component={component}
              isEditing={true}
              onEdit={onEditComponent}
              onDelete={onDeleteComponent}
              onMoveUp={visibleIndex > 0 ? () => onMoveComponent(component.id, 'up') : undefined}
              onMoveDown={
                visibleIndex < visibleComponents.length - 1
                  ? () => onMoveComponent(component.id, 'down')
                  : undefined
              }
              onSaveToLibrary={onSaveToLibrary}
              onDuplicate={component.type === 'conversion-popup' ? undefined : onDuplicateComponent}
            />
          </div>
          {onAddComponentAt && (
            <InsertComponentLine
              position={getActualInsertPosition(visibleIndex + 1)}
              onInsert={onAddComponentAt}
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
};
